/**
 * Iconify offline icon bootstrap.
 *
 * @iconify-icon/react renders icons via a web component that, by default,
 * lazy-fetches SVG data from api.iconify.design at runtime. Until that fetch
 * resolves the element is an empty inline-block — and when the API is slow,
 * blocked, or the box is offline the icon stays blank forever (worst case: an
 * unlabeled destructive control). We eliminate that third-party runtime
 * dependency entirely by registering the icon data from our OWN bundle before
 * the app mounts, so every icon resolves synchronously from the in-memory cache.
 *
 * Strategy:
 *   - simple-icons / tabler / mdi: only the used subset is bundled (all brand
 *     logos are literal refs, never runtime-computed) — registered synchronously
 *     here so brand glyphs paint immediately. Subsets are generated by
 *     scripts/gen-icon-subsets.mjs (a prebuild step) — never hand-maintained.
 *   - lucide: the FULL collection is required (404 runtime-computed refs) and is
 *     loaded as a lazy chunk (iconifyLucide.ts) to keep the main bundle small.
 *   - TopBar chrome lucide icons are ALSO inlined below via addIcon() so the
 *     most-visible chrome paints with zero flash even before the lucide chunk
 *     resolves.
 */
import { addCollection, addIcon, type IconifyJSON } from "@iconify-icon/react";
import simpleIcons from "./iconifySubsets/simple-icons.json";
import tabler from "./iconifySubsets/tabler.json";
import mdi from "./iconifySubsets/mdi.json";

// ── Bundled subset collections (brand/chrome logos, always literal refs) ─────
addCollection(simpleIcons as IconifyJSON);
addCollection(tabler as IconifyJSON);
addCollection(mdi as IconifyJSON);

// ── Full lucide collection — lazy chunk, registers once, cached ──────────────
// Fire-and-forget: the chunk is same-origin and small; icons that render before
// it resolves are covered by the inlined chrome set below, then fill in on load.
void import("./iconifyLucide")
  .then(({ registerLucide }) => registerLucide())
  .catch(() => {
    // A failed lucide chunk load is non-fatal — chrome icons stay rendered from
    // the inlined set below; nothing throws into React.
  });

// ── Inlined TopBar chrome icons (zero-flash first paint) ─────────────────────
// These duplicate a handful of lucide glyphs into the main bundle so the always-
// visible TopBar chrome paints instantly, before the lazy lucide chunk arrives.

addIcon("lucide:globe", {
  width: 24,
  height: 24,
  body: '<g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20a14.5 14.5 0 0 0 0-20M2 12h20"/></g>',
});

addIcon("lucide:palette", {
  width: 24,
  height: 24,
  body: '<g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><path d="M12 22a1 1 0 0 1 0-20a10 9 0 0 1 10 9a5 5 0 0 1-5 5h-2.25a1.75 1.75 0 0 0-1.4 2.8l.3.4a1.75 1.75 0 0 1-1.4 2.8z"/><circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/></g>',
});

addIcon("lucide:bell", {
  width: 24,
  height: 24,
  body: '<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.268 21a2 2 0 0 0 3.464 0m-10.47-5.674A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326"/>',
});

addIcon("lucide:search", {
  width: 24,
  height: 24,
  body: '<g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><path d="m21 21l-4.34-4.34"/><circle cx="11" cy="11" r="8"/></g>',
});

addIcon("lucide:menu", {
  width: 24,
  height: 24,
  body: '<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5h16M4 12h16M4 19h16"/>',
});

addIcon("lucide:x", {
  width: 24,
  height: 24,
  body: '<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 6L6 18M6 6l12 12"/>',
});

addIcon("lucide:user", {
  width: 24,
  height: 24,
  body: '<g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></g>',
});

addIcon("lucide:shield", {
  width: 24,
  height: 24,
  body: '<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>',
});

addIcon("lucide:monitor", {
  width: 24,
  height: 24,
  body: '<g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><rect width="20" height="14" x="2" y="3" rx="2"/><path d="M8 21h8m-4-4v4"/></g>',
});

addIcon("lucide:inbox", {
  width: 24,
  height: 24,
  body: '<g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11"/></g>',
});

addIcon("lucide:log-out", {
  width: 24,
  height: 24,
  body: '<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m16 17l5-5l-5-5m5 5H9m0 9H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>',
});

addIcon("lucide:check", {
  width: 24,
  height: 24,
  body: '<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 6L9 17l-5-5"/>',
});

addIcon("lucide:construction", {
  width: 24,
  height: 24,
  body: '<g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><rect width="20" height="8" x="2" y="6" rx="1"/><path d="M17 14v7M7 14v7M17 3v3M7 3v3m3 8L2.3 6.3M14 6l7.7 7.7M8 6l8 8"/></g>',
});
